import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

import { catchError, finalize, map, of } from "rxjs";
import { AuthenticationErrors } from "../../../core/authentication/authentication-errors";
import { AuthenticationServerService } from "../../../core/authentication/authentication-server.service";
import { LoginFlowNotSupportedError } from "../../../core/errors/login-flow-not-supported-error";
import { IHttpErrorResponseBody } from "../../../core/http-error-response-body.model";
import { WINDOW } from "../../../shared/app.window";
import { BaseComponent } from "../../../shared/base.component";
import { PasswordInputComponent } from "../../../shared/FormControls/password-input/password-input.component";
import { requiredWithCustomMessage } from "../../../shared/FormControls/validators";
import { isDefined, isEmptyString } from "../../../shared/helpers";
import { NotificationBarComponent } from "../../../shared/notification-bar";
import { LoginAssistantService } from "../../login-assistant/login-assistant.service";
import { IPasswordPolicyDto, PasswordPolicy, PasswordUpdateService } from "../../password-update/password-update.service";
import { LoginService } from "../login.service";

@Component({
  selector: "app-password-login",
  templateUrl: "./password-login.component.html",
  styleUrls: ["./password-login.component.scss"]
})
export class PasswordLoginComponent extends BaseComponent implements OnInit, AfterViewInit {

  override componentTranslationRoot = "login.password-login";
  @ViewChild("notificationBar", { static: false }) notificationBar!: NotificationBarComponent;
  @ViewChild("userAccountInput", { static: true }) userAccountInput!: ElementRef;
  @ViewChild("passwordInput", { static: true }) passwordInput!: PasswordInputComponent;
  @Output() tokenAcquired = new EventEmitter<string>();
  private username = "";

  @Input() set accountName(value: string) {
    this.username = value?.trim() ?? "";
    this.loginForm.controls.userAccount.setValue(this.username);
  }
  formValidityActivated = false;
  loginInProgress = false;
  isUserNameCheckInProgress = false;

  loginForm = this.formBuilder.group({
    userAccount: ["", [requiredWithCustomMessage("enter-login-name")]],
    password: [""]
  }, { updateOn: "blur", });
  passwordResetActivated = false;
  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,

    private authenticationServerService: AuthenticationServerService,
    private router: Router,
    private passwordUpdateService: PasswordUpdateService,
    @Inject(WINDOW) private window: Window,
    private loginService: LoginService,
    private loginAssistantService: LoginAssistantService
  ) {
    super(translateService);
  }

  ngAfterViewInit(): void {
    if (isEmptyString((this.loginForm.controls.userAccount.value ?? "").trim())) {
      this.userAccountInput.nativeElement.focus();
    } else {
      setTimeout(() => { // make the validation in another cycle  not to interfere wiht basic page load cycle
        this.checkUserLoginUrl();
      }, 1);

      this.passwordInput.focus();
    }
  }

  ngOnInit(): void {
    this.subscriptionsToRelease.push(
      this.loginForm.valueChanges.subscribe(() => {
        this.notificationBar.hide();
      })
    );
  }

  showUserAccountCheckIndicator() {
    this.isUserNameCheckInProgress = true;
  }

  hideUserAccountCheckIndicator() {
    this.isUserNameCheckInProgress = false;
  }

  checkUserLoginUrl(additionalErrorHandler = () => { }) {
    const loginName = this.loginForm.controls.userAccount.value!;
    if (!isEmptyString(loginName?.trim())) {
      this.showUserAccountCheckIndicator();
      this.loginService.checkLoginUrl(loginName)
        .pipe(
          finalize(() => this.hideUserAccountCheckIndicator()),
          catchError(error => of(this.handleLoginUrlCheckErrors(error, additionalErrorHandler)))
        )
        .subscribe();
    }
  }

  private handleLoginUrlCheckErrors(error: Error, additionalErrorHandler: (error: Error) => unknown) {
    if (error instanceof LoginFlowNotSupportedError) {
      const errorText = this.translate("windows-web-sign-in-and-azure-as-idp", {}, "authentication-api");
      this.notificationBar.showErrorMessage({ showIcon: false, text: errorText });
    }
    additionalErrorHandler(error);
    //rest of the errors should silently die. user did not explicitly ask for this check so no need to show error messages here
  }

  login() {
    this.formValidityActivated = true;
    this.activateValidatorOnPasswordField();
    if (this.loginForm.valid) {
      this.notificationBar.hide();
      const loginName = this.loginForm.controls.userAccount.value!;
      const password = this.loginForm.controls.password.value!;
      this.loginInProgress = true;

      if (!isEmptyString(loginName) && !isEmptyString(password)) {
        this.authenticationServerService.authenticateUsingUsernameAndPassword(loginName, password)
          .pipe(finalize(() => { this.loginInProgress = false; }))
          .pipe(
            map(token => this.tokenAcquired.emit(token)),
            catchError(response => of(this.handleTokenAcquisitionError(response)))
          ).subscribe();
      }
    }

  }

  private activateValidatorOnPasswordField() {
    this.loginForm.controls.password.setValidators([requiredWithCustomMessage("enter-password")]);
    this.loginForm.controls.password.updateValueAndValidity();
  }

  handleTokenAcquisitionError(response: HttpErrorResponse) {
    const responseBody = response?.error as IHttpErrorResponseBody;
    const responseCode = responseBody?.Code;
    if (responseCode === "PasswordExpired") {

      this.redirectToPasswordUpdateScreen(responseBody);
    } else {
      this.notificationBar.showErrorMessage({
        icon: "icon-meh",
        text: this.getErrorMessageText(responseCode)
      });
    }
  }

  private getErrorMessageText(authLoginErrorKey: string) {
    const errorKey = AuthenticationErrors.getErrorTranslationKey(authLoginErrorKey);
    return this.translate(errorKey, {}, "authentication-api");
  }

  private redirectToPasswordUpdateScreen(responseBody: IHttpErrorResponseBody) {
    const loginName = this.loginForm.controls.userAccount.value!;
    const policyDto: IPasswordPolicyDto = responseBody.Data! as unknown as IPasswordPolicyDto;
    this.passwordUpdateService.selectedUserName = loginName;
    this.passwordUpdateService.passwordPolicy = PasswordPolicy.fromDto(policyDto);
    this.router.navigate(["/update-password"]);
  }

  get isUserNameInValid(): boolean {
    return isDefined(this.loginForm.controls.userAccount.errors)
      && this.loginForm.controls.userAccount.dirty;
  }

  get userAccountValidationErrors(): string {
    return this.translate("enter-login-name");
  }

  get isFormSubmitDisabled(): boolean {
    return this.formValidityActivated && (this.loginForm.invalid || this.loginInProgress);
  }

  resetPassword(): void {
    this.passwordResetActivated = true;
    if (isEmptyString(this.loginForm.controls.userAccount.value?.trim())) {
      this.redirectToPasswordReset();
    } else {
      this.checkUserLoginUrl(() => this.redirectToPasswordReset());
    }

  }

  redirectToPasswordReset(): void {
    this.passwordResetActivated = false;
    const callbackUrl = this.window.location.href;
    const loginName = this.loginForm.controls.userAccount.value;
    const loginNamePart = isEmptyString(loginName) ? "" : `&pgautofulluser=${encodeURIComponent(loginName!)}`;
    const newLocation = `https://reset.moo.nl/_layouts/PG/login.aspx?ReturnUrl=${encodeURIComponent(callbackUrl)}&pgautopop=8${loginNamePart}`;
    this.window.location.href = newLocation;
  }

  goToPreviousScreen() {
    this.loginAssistantService.redirectToLastAssistantScreen();
  }

  get hasLoginAssistantHistory(): boolean {
    return this.loginAssistantService.hasLoginAssistantHistory();
  }

  checkLoginUrlAndTrySubmit(event: Event) {
    this.checkUserLoginUrl(() => this.tryTriggerSubmit(event));
  }

  tryTriggerSubmit(event: Event) {
    this.userAccountInput.nativeElement.blur();
    this.passwordInput.blur();
    (event!.target as HTMLInputElement).focus();
    this.loginForm.controls.userAccount.markAsDirty();
    this.loginForm.markAllAsTouched();
    this.login();
  }

  openQrLogin(): void {
    this.loginService.navigateToQrLogin();
  }
}
